import React from 'react';
import PropTypes from 'prop-types';
import Price from '../../Price';
import '../../shared/Card.css';
import './ImageSelector.css';

/**
 * ImageSelector — Configurator
 *
 * Selectable image tile (e.g. a letter or motif option): square white image
 * well, title and a label-size price underneath.
 *
 * Figma component set: 4290:24169
 * Variants: State = Default (4290:24170) | Hover (4290:24180) | Selected (4290:24190)
 *           Details / Title / Price — boolean toggles
 *
 * The Figma booleans map to data: the details block renders when `title` or
 * `price` is set. Hover is CSS-only; Selected comes from the `selected` prop.
 *
 * `variant="compact"` is the Figma "ImageSelectorV2" option card (Crownstudent-MCP
 * 152:2818 / 372:11113): a small image + its name, sized by its row — a column
 * (40px image) on mobile, a row (52px image) from 1024px. One responsive
 * component; the default `tile` is unchanged.
 */
const ImageSelector = ({
  image,
  imageAlt = '',
  title,
  price,
  selected = false,
  imageFit = 'contain',
  variant = 'tile',
  onClick,
  className = '',
  ...props
}) => {
  const hasDetails = Boolean(title || price);

  const classes = [
    'ds-card',
    'ds-card--interactive',
    'cfg-image-selector',
    selected && 'ds-card--selected',
    imageFit === 'cover' && 'cfg-image-selector--cover',
    variant === 'compact' && 'cfg-image-selector--compact',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-pressed={selected}
      {...props}
    >
      <span className="cfg-image-selector__image">
        {image && <img className="cfg-image-selector__img" src={image} alt={imageAlt} />}
      </span>

      {hasDetails && (
        <span className="cfg-image-selector__details">
          {title && <span className="cfg-image-selector__title typo-body-sm">{title}</span>}
          {price && <Price type="small" price={price} className="cfg-image-selector__price" />}
        </span>
      )}
    </button>
  );
};

ImageSelector.propTypes = {
  /** Option image URL (contained with a 12.5% inset on white) */
  image: PropTypes.string,
  /** Alt text for the image */
  imageAlt: PropTypes.string,
  /** Option name (typo-body-sm) */
  title: PropTypes.string,
  /** Formatted price, e.g. "49 kr" — DS Price at label size */
  price: PropTypes.string,
  /** Selected state — gray-10 border */
  selected: PropTypes.bool,
  /** 'contain' (default): cut-out artwork with the 12.5% inset on white; 'cover': a photo filling the well */
  imageFit: PropTypes.oneOf(['contain', 'cover']),
  /** 'tile' (default): the 120px tile; 'compact': the Figma V2 option card — a column (40px image) on mobile, a row (52px image) from 1024px, sized by its row */
  variant: PropTypes.oneOf(['tile', 'compact']),
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default ImageSelector;
